<template>
  <SloFormPresentational
    v-model:definition="definition"
    v-model:description="description"
    v-model:exceptions="exceptions"
    v-model:measure-method="measureMethod"
    v-model:measure-type="measureType"
    v-model:name="name"
    v-model:visible="visible"
    :error-messages="errorMessages"
    :loading="loading"
    :server-error="serverError"
    @submit="submit"
  >
    <div v-if="isUpdateForm" class="row">
      <div class="col">
        <label class="form-label d-inline-flex">
          <span> Penalties </span>
          <IconButton
            icon-name="material-symbols:add"
            class="ms-1 text-success"
            @keypress.stop
            @click.stop="goToPenaltiesForm()"
          />
        </label>
        <SloPenaltiesDataTable
          :penalties="penalties"
          @update="goToPenaltiesForm"
          @delete="deletePenalty"
        />
      </div>
    </div>
  </SloFormPresentational>
</template>
<script lang="ts" setup>
import {Detail} from '@/api/errorMessage';
import {ServiceLevelObjectiveCreateUpdate} from '@/stores/userManagement/userManagement';

const props = defineProps<{
  isUpdateForm: boolean;
  id?: number;
}>();

const router = useRouter();
const route = useRoute();

// Stores
const serviceLevelObjectivesStore = useServiceLevelObjectivesStore();
const getServiceLevelObjectives = computed(
  () => serviceLevelObjectivesStore.getServiceLevelObjectives,
);

const loading = ref(false);

// Necessary to circumvent :corePath+ router history.
const goToPenaltiesForm = (id?: number | undefined) => {
  const path = id
    ? route.path.replace(`/${props.id}`, `/${id}`)
    : route.path.replace('/update/', '/create/');
  router.push({path, query: {p: 1, sloId: props.id}});
};

// The local refs used by SloPenaltiesDataTable
const slo = ref<ServiceLevelObjective>();
const penalties = computed(() => slo.value?.penalties || []);
// Remove the slo to update the penalties computed
const deletePenalty = (id: number) => {
  if (slo.value !== undefined) {
    const index = slo.value.penalties.findIndex((p) => p.id === id);
    if (index >= 0) slo.value.penalties.splice(index, 1);
  }
};

// Form data
const definition = ref('');
const description = ref('');
const exceptions = ref('');
const measureMethod = ref('');
const measureType = ref('uptime_monitoring');
const name = ref('');
const visible = ref(false);
const errorMessages = ref(new Map());
const serverError = ref(false);

const submit = async (form: globalThis.Ref<HTMLFormElement | null>) => {
  try {
    loading.value = true;
    const slo: ServiceLevelObjectiveCreateUpdate = {
      name: name.value,
      description: description.value,
      definition: definition.value,
      measurement_type: measureType.value,
      measure_method: measureMethod.value,
      exceptions: exceptions.value,
      customer_visibility: Boolean(visible.value),
    };
    if (!props.isUpdateForm) {
      await serviceLevelObjectivesStore.createServiceLevelObjective(slo);
      router.go(-1);
    } else {
      const updateObjective: ServiceLevelObjectiveCreateUpdate = {
        ...slo,
        id: Number(props.id),
      };
      await serviceLevelObjectivesStore.updateServiceLevelObjective(updateObjective);
      router.go(-1);
    }
  } catch (errorMessage: any) {
    serverError.value = true;
    errorMessages.value = new Map();

    form.value?.classList.remove('was-validated');
    if (typeof errorMessage !== 'string' && errorMessage.length > 0) {
      errorMessage.forEach((d: Detail) => errorMessages.value.set(d.loc[1], d.msg));
    }
    form.value?.classList.remove('was-validated');
    console.log(errorMessage);
  } finally {
    loading.value = false;
  }
};

const load = async () => {
  if (props.isUpdateForm === true) {
    // Try find the item in store state
    slo.value = getServiceLevelObjectives.value.find((i) => i.id === props.id);
    // If the item is missing, try to fetch it
    if (slo.value === undefined && props.id !== undefined) {
      try {
        const data = await serviceLevelObjectivesStore.readServiceLevelObjectiveById(props.id);
        slo.value = data;
      } catch (error) {
        console.log(error);
      }
    }
    if (slo.value !== undefined) {
      // Set the form data
      name.value = slo.value.name;
      measureType.value = slo.value.measurement_type;
      definition.value = slo.value.definition;
      visible.value = slo.value.customer_visibility;
      description.value = slo.value.description;
      measureMethod.value = slo.value.measure_method;
      exceptions.value = slo.value.exceptions;
    }
  }
};

onMounted(() => load());
</script>
